// La planta de la oficina para el hueco que tiene en pantalla. `generarMundo` solo se vuelve a llamar cuando cambia
// algo que la planta usa (la FIRMA): tamaño, panel, salas y quién se sienta dónde. Un mensaje de cualquier agente
// cambia `contratados` pero no la firma: ni se regenera el mundo ni se repinta el lienzo.
import { useEffect, useMemo, useState, type RefObject } from 'react';
import type { ContratadoDTO, GrupoDTO, SalaDTO } from '../compartido/vocabulario';
import { seVa } from '../marco/situacion';
import { generarMundo, type Mundo } from './generar';

export interface Hueco {
  w: number;
  h: number;
}

/** Todo lo que `generarMundo` lee, en una cadena: misma firma ⇒ misma planta. */
export function firmaMundo(hueco: Hueco, panelIzqPx: number, salas: readonly SalaDTO[], lista: readonly ContratadoDTO[], grupos: readonly GrupoDTO[] = [], abierta: string | null = null): string {
  return JSON.stringify([
    hueco.w,
    hueco.h,
    panelIzqPx,
    salas.map((s) => [s.projectId, s.nombre, s.color, s.principal]),
    lista.map((c) => [c.agentId, c.projectId, c.grupoId ?? null, c.mesa, seVa(c)]),
    // 04-11 — los grupos de mesas: su nombre y su orden cambian la planta (el cartel, dónde va cada isla).
    grupos.map((g) => [g.id, g.projectId, g.nombre, g.orden]),
    // 04-11 — la sala secundaria abierta como carpeta (las demás, plegadas si no cabe todo).
    abierta,
  ]);
}

export function useMundo(
  marco: RefObject<HTMLDivElement>,
  salas: readonly SalaDTO[],
  lista: readonly ContratadoDTO[],
  panelIzqPx: number,
  grupos: readonly GrupoDTO[] = [],
  abierta: string | null = null,
): { mundo: Mundo | null; hueco: Hueco } {
  const [hueco, setHueco] = useState<Hueco>({ w: 0, h: 0 });
  // SE MIDE EL MARCO, NO LA CAJA QUE SE DESPLAZA (parpadeo 28-09): el tamaño útil de la caja cambia cuando le salen
  // barras de desplazamiento (en Windows ocupan sitio), y el mundo se calcula con ese tamaño: el mundo cambiaba las
  // barras, las barras cambiaban el mundo, 60 veces por segundo. El marco no tiene barras.
  useEffect(() => {
    const el = marco.current;
    if (!el) return;
    const medir = () => setHueco((h) => (h.w === el.clientWidth && h.h === el.clientHeight ? h : { w: el.clientWidth, h: el.clientHeight }));
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    medir();
    return () => ro.disconnect();
  }, [marco]);

  const firma = firmaMundo(hueco, panelIzqPx, salas, lista, grupos, abierta);
  const mundo = useMemo(
    () =>
      hueco.w && hueco.h
        ? generarMundo({
            anchoPx: hueco.w,
            altoPx: hueco.h,
            panelIzqPx,
            salas: salas.map((s) => ({ projectId: s.projectId, nombre: s.nombre, color: s.color, principal: s.principal })),
            // Los que se van no ocupan mesa: esperan en la fila de la salida, apagados (Pau, 29-10).
            contratados: lista.filter((c) => !seVa(c)).map((c) => ({ agentId: c.agentId, projectId: c.projectId, mesa: c.mesa, grupoId: c.grupoId ?? null })),
            saliendo: lista.filter(seVa).map((c) => c.agentId),
            grupos: grupos.map((g) => ({ id: g.id, projectId: g.projectId, nombre: g.nombre, orden: g.orden })),
            abierta,
          })
        : null,
    // La firma recoge todo lo que se lee aquí dentro (ver firmaMundo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firma],
  );
  return { mundo, hueco };
}
